import { SET_USER_ATTRIBUTE, SET_ACCOUNT_USER_ATTRIBUTE } from 'vx/mutations'
import { mapMutations } from 'vuex'
import { isObject } from 'lodash'

/**
 * User state mapper. When used the component MUST define a "stateProperty" which is the
 * property within the state object that we pull from. It also must define another
 * "mutationProperty" which is the mutation constant to use when setting
 * values.
 */
export default {
  methods: {
    ...mapMutations('user', [SET_USER_ATTRIBUTE]),
    ...mapMutations('user/accountUsers', [SET_ACCOUNT_USER_ATTRIBUTE])
  },

  computed: {
    /**
     * Get and set a users name on their profile.
     */
    name: {
      get () {
        return this[this.stateProperty].profile.name
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.name', value })
      }
    },

    /**
     * Get and set a users surname on their profile.
     */
    surname: {
      get () {
        return this[this.stateProperty].profile.surname
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.surname', value })
      }
    },

    /**
     * Get and set a users mobile on their profile.
     */
    mobile: {
      get () {
        return this[this.stateProperty].mobile
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'mobile', value })
      }
    },

    /**
     * Get and set a users email.
     */
    email: {
      get () {
        return this[this.stateProperty].email
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'email', value })
      }
    },

    /**
     * Get and set a users company name on their profile.
     */
    companyName: {
      get () {
        return this[this.stateProperty].profile.company_name
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.company_name', value })
      }
    },

    /**
     * Get and set a users company phone on their profile.
     */
    companyPhone: {
      get () {
        return this[this.stateProperty].profile.company_phone
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.company_phone', value })
      }
    },

    /**
     * Get and set a users company ABN on their profile.
     */
    companyAbn: {
      get () {
        return this[this.stateProperty].profile.company_abn
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.company_abn', value })
      }
    },

    /**
     * Get and set a users address line 1 on their profile.
     */
    addressLineOne: {
      get () {
        return this[this.stateProperty].profile.address_line_1
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.address_line_1', value })
      }
    },

    /**
     * Get and set a users address line 2 on their profile.
     */
    addressLineTwo: {
      get () {
        return this[this.stateProperty].profile.address_line_2
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.address_line_2', value })
      }
    },

    /**
     * Get and set a users state on their profile.
     */
    state: {
      get () {
        return this[this.stateProperty].profile.state
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.state', value })
      }
    },

    /**
     * Get and set a users suburb on their profile.
     */
    suburb: {
      get () {
        return this[this.stateProperty].profile.suburb
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.suburb', value })
      }
    },

    /**
     * Get and set a users postcode on their profile.
     */
    postcode: {
      get () {
        return this[this.stateProperty].profile.postcode
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.postcode', value })
      }
    },

    /**
     * Get and set a users commission amount on their profile.
     */
    commission: {
      get () {
        return this[this.stateProperty].profile.commission_amount
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.commission_amount', value })
      }
    },

    /**
     * Get and set a users account name on their profile.
     */
    accountName: {
      get () {
        return this[this.stateProperty].profile.account_name
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.account_name', value })
      }
    },

    /**
     * Get and set a users account number on their profile.
     */
    accountNumber: {
      get () {
        return this[this.stateProperty].profile.account_number
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.account_number', value })
      }
    },

    /**
     * Get and set a users BSB on their profile.
     */
    bsb: {
      get () {
        return this[this.stateProperty].profile.bsb
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.bsb', value })
      }
    },

    /**
     * Get and set a users notify of job status on their profile.
     */
    notifyJobStatus: {
      get () {
        return this[this.stateProperty].profile.notify_job_status
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.notify_job_status', value })
      }
    },

    /**
     * Get and set a users notify of load deliveries on their profile.
     */
    notifyLoadDeliveries: {
      get () {
        return this[this.stateProperty].profile.notify_load_deliveries
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.notify_load_deliveries', value })
      }
    },

    /**
     * Get and set a users role.
     */
    roleId: {
      get () {
        return this[this.stateProperty].role_id
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'role_id', value })
      }
    },

    /**
     * Get and set a users parent.
     */
    parentId: {
      get () {
        return this[this.stateProperty].parent_id
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'parent_id', value })
      }
    },

    /**
     * Get and set a users licence.
     */
    licence: {
      get () {
        if (isObject(this[this.stateProperty].driver_profile)) {
          return this[this.stateProperty].driver_profile.licence || null
        }

        return null
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'driver_profile.licence', value })
      }
    },

    /**
     * Get and set a users licence expiry.
     */
    licenceExpiry: {
      get () {
        if (isObject(this[this.stateProperty].driver_profile)) {
          return this[this.stateProperty].driver_profile.licence_expiry || null
        }

        return null
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'driver_profile.licence_expiry', value })
      }
    },

    /*
     * Get and set a users notify of job completed on their profile.
     */
    notifyJobCompleted: {
      get () {
        return this[this.stateProperty].profile.notify_job_completed
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.notify_job_completed', value })
      }
    },

    /*
     * Get and set a users notify of child accounts on their profile.
     */
    notifyChildren: {
      get () {
        return this[this.stateProperty].profile.notify_children
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.notify_children', value })
      }
    },

    /*
     * Get and set whether a user receives the daily public job email or not.
     */
    notifyPublicJobs: {
      get () {
        return this[this.stateProperty].profile.notify_public_jobs
      },

      set (value) {
        this[this.mutationProperty]({ attribute: 'profile.notify_public_jobs', value })
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/user/state-mapper.js